import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { isDateInRange } from "../utils/academicYearUtils";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import DateField from "./DateField";
import HelpTooltip from "./HelpTooltip";

const PaymentDialog = ({
  open,
  onClose,
  onSubmit,
  selectedAssignment,
  paymentData,
  setPaymentData,
  banks,
  academicYearStartDate,
  academicYearEndDate,
  formatCurrency,
}) => {
  const bankOptions = Array.isArray(banks) ? banks : [];
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (open) setSubmitAttempted(false);
  }, [open]);

  // Validation helpers
  const validateAmount = (amount) => {
    const numAmount = Number.parseFloat(amount);
    if (!amount || Number.isNaN(numAmount)) return "Amount is required";
    if (numAmount <= 0) return "Amount must be greater than 0";
    if (numAmount < 1) return "Minimum payment amount is ₹1";
    if (selectedAssignment && numAmount > selectedAssignment.balance_due) {
      return `Amount cannot exceed balance due (${formatCurrency(selectedAssignment.balance_due)})`;
    }
    // Check decimal places (max 2)
    const decimalPart = amount.toString().split(".")[1];
    if (decimalPart && decimalPart.length > 2) {
      return "Amount cannot have more than 2 decimal places";
    }
    return null;
  };

  const validateTransactionReference = (ref, paymentMethod) => {
    // Require transaction reference for UPI, Bank Transfer, and Cheque
    if (
      (paymentMethod === "UPI" ||
        paymentMethod === "BANK_TRANSFER" ||
        paymentMethod === "CHEQUE") &&
      !ref?.trim()
    ) {
      return "Transaction reference is required for UPI, Bank Transfer, and Cheque payments";
    }

    // Format validation when provided
    if (ref?.trim()) {
      if (ref.trim().length < 4) {
        return "Transaction reference must be at least 4 characters";
      }
      if (ref.trim().length > 50) {
        return "Transaction reference cannot exceed 50 characters";
      }
      // Alphanumeric and common special characters only
      if (!/^[a-zA-Z0-9\-_/]+$/.test(ref.trim())) {
        return "Transaction reference can only contain letters, numbers, hyphens, underscores, and forward slashes";
      }
    }
    return null;
  };

  const validatePaymentDate = (dateValue) => {
    if (!dateValue?.trim()) return "Payment date is required";

    const paymentDate = moment(dateValue, "YYYY-MM-DD", true);
    if (!paymentDate.isValid()) return "Invalid payment date format";

    const startDate = academicYearStartDate
      ? moment(academicYearStartDate).startOf("day")
      : null;
    const endDate = academicYearEndDate
      ? moment(academicYearEndDate).startOf("day")
      : null;

    const today = moment().startOf("day");
    const isFutureAY =
      startDate?.isValid() && today.isBefore(startDate, "day");

    // For future academic year assignments, allow payment date before the year starts
    if (!isFutureAY && startDate?.isValid() && paymentDate.isBefore(startDate, "day")) {
      return `Payment date must be on or after ${startDate.format("DD MMM YYYY")}`;
    }

    if (endDate?.isValid() && paymentDate.isAfter(endDate, "day")) {
      return `Payment date must be on or before ${endDate.format("DD MMM YYYY")}`;
    }

    if (startDate?.isValid() && endDate?.isValid()) {
      const today = moment().startOf("day");
      const isCurrentAcademicYear = isDateInRange(startDate, endDate, today);
      if (isCurrentAcademicYear && paymentDate.isAfter(today, "day")) {
        return "Payment date cannot be in the future";
      }
    }

    return null;
  };

  const validateBankSelection = (bankId, paymentMethod) => {
    if (paymentMethod === "BANK_TRANSFER") {
      if (!bankId) {
        return "Bank is required for Bank Transfer payments";
      }
    }
    return null;
  };

  const amountError = validateAmount(paymentData.amountPaid);
  const transactionRefError = validateTransactionReference(
    paymentData.transactionReference,
    paymentData.paymentMethod,
  );
  const bankError = validateBankSelection(
    paymentData.bankId,
    paymentData.paymentMethod,
  );
  const paymentDateError = validatePaymentDate(paymentData.paymentDate || "");

  const isFormValid =
    !amountError && !transactionRefError && !bankError && !paymentDateError;

  const handleSubmit = () => {
    setSubmitAttempted(true);
    if (isFormValid) onSubmit();
  };

  const minPaymentDate = (() => {
    if (!academicYearStartDate) return undefined;
    const startDate = moment(academicYearStartDate).startOf("day");
    const today = moment().startOf("day");
    // For future academic year assignments allow payment from any past date
    if (today.isBefore(startDate, "day")) return undefined;
    return startDate;
  })();

  const maxPaymentDate = (() => {
    const startDate = academicYearStartDate
      ? moment(academicYearStartDate).startOf("day")
      : null;
    const endDate = academicYearEndDate
      ? moment(academicYearEndDate).startOf("day")
      : null;
    const today = moment().startOf("day");

    if (!startDate?.isValid() || !endDate?.isValid()) {
      return today;
    }

    const isCurrentAcademicYear = isDateInRange(startDate, endDate, today);

    // For future academic year assignments, cap at today
    const isFutureAY = today.isBefore(startDate, "day");
    if (isFutureAY) return today;

    return isCurrentAcademicYear ? today : endDate;
  })();

  const isFutureAcademicYear = (() => {
    if (!academicYearStartDate) return false;
    const startDate = moment(academicYearStartDate).startOf("day");
    return moment().startOf("day").isBefore(startDate, "day");
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: { xs: "1rem", sm: "1.5rem" } }}>
        Record Payment
      </DialogTitle>
      <DialogContent>
        {selectedAssignment && (
          <Box sx={{ pt: 2 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontSize: { xs: "0.82rem", sm: "1rem" } }}
            >
              Fee Type: {selectedAssignment.fee_type}
            </Typography>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontSize: { xs: "0.82rem", sm: "1rem" } }}
            >
              Total Balance Due:{" "}
              {formatCurrency(selectedAssignment.balance_due)}
            </Typography>
            {selectedAssignment.previous_year_pending > 0 && (
              <Box
                sx={{
                  ml: 2,
                  mt: 1,
                  p: 1,
                  bgcolor: "warning.lighter",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  display="block"
                  color="warning.dark"
                >
                  Previous Year Pending ({selectedAssignment.previous_year_from}
                  ): {formatCurrency(selectedAssignment.previous_year_pending)}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  Current Year Balance:{" "}
                  {formatCurrency(selectedAssignment.current_year_balance)}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="info.main"
                  sx={{ mt: 0.5, fontStyle: "italic" }}
                >
                  Note: Payment will first clear previous year pending fees
                </Typography>
              </Box>
            )}
            {isFutureAcademicYear && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: "info.lighter",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "info.light",
                }}
              >
                <Typography
                  variant="caption"
                  display="block"
                  color="info.dark"
                  sx={{ fontWeight: 600 }}
                >
                  Advance Payment
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="info.dark"
                >
                  This will be recorded as an advance payment for{" "}
                  {selectedAssignment.academic_year}. The academic year has not
                  started yet.
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ lg: 6, xs: 12, sm: 6, md: 6 }}>
                <TextField
                  fullWidth
                  label="Amount to Pay"
                  size="small"
                  type="number"
                  value={paymentData.amountPaid}
                  onChange={(e) =>
                    setPaymentData({
                      ...paymentData,
                      amountPaid: e.target.value,
                    })
                  }
                  slotProps={{
                    input: {
                      max: selectedAssignment.balance_due,
                      min: 1,
                      step: "0.01",
                    },
                  }}
                  error={submitAttempted && !!amountError}
                  helperText={submitAttempted ? amountError : ""}
                  required
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ lg: 6, xs: 12, sm: 6, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="Payment Method"
                  value={paymentData.paymentMethod}
                  onChange={(e) =>
                    setPaymentData({
                      ...paymentData,
                      paymentMethod: e.target.value,
                      bankId:
                        e.target.value === "UPI" ||
                        e.target.value === "BANK_TRANSFER"
                          ? paymentData.bankId
                          : "",
                    })
                  }
                  required
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    },
                  }}
                >
                  <MenuItem value="CASH">Cash</MenuItem>
                  <MenuItem value="CARD">Card</MenuItem>
                  <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                  <MenuItem value="UPI">UPI</MenuItem>
                </TextField>
              </Grid>
              {(paymentData.paymentMethod === "UPI" ||
                paymentData.paymentMethod === "BANK_TRANSFER") && (
                <Grid size={{ lg: 6, xs: 12, sm: 6, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Bank"
                    value={paymentData.bankId || ""}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        bankId: e.target.value,
                      })
                    }
                    required={paymentData.paymentMethod === "BANK_TRANSFER"}
                    error={submitAttempted && !!bankError}
                    helperText={
                      (submitAttempted ? bankError : "") ||
                      (bankOptions.length === 0
                        ? "No active banks configured. Please configure a bank first."
                        : "")
                    }
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.95rem", sm: "1rem" },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.95rem", sm: "1rem" },
                      },
                    }}
                  >
                    {bankOptions.map((bank) => (
                      <MenuItem key={bank.bank_id} value={String(bank.bank_id)}>
                        {bank.bank_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid size={{ lg: 6, xs: 12, sm: 6, md: 6 }}>
                <DateField
                  label="Payment Date"
                  name="paymentDate"
                  size="small"
                  value={paymentData.paymentDate || ""}
                  onChange={(e) =>
                    setPaymentData({
                      ...paymentData,
                      paymentDate: e.target.value,
                    })
                  }
                  minDate={minPaymentDate}
                  maxDate={maxPaymentDate}
                  includeProvider={true}
                  fullWidth
                  required
                  error={submitAttempted && !!paymentDateError}
                  helperText={
                    (submitAttempted ? paymentDateError : "") ||
                    (isFutureAcademicYear
                      ? `Advance payment — allowed up to ${maxPaymentDate.format("DD MMM YYYY")}`
                      : academicYearStartDate && academicYearEndDate
                        ? `Allowed: ${moment(academicYearStartDate).format("DD MMM YYYY")} to ${maxPaymentDate.format("DD MMM YYYY")}`
                        : undefined)
                  }
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ lg: 6, xs: 12, sm: 6, md: 6 }}>
                <TextField
                  fullWidth
                  label={
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      Transaction Reference
                      <HelpTooltip title="Enter UPI transaction ID, cheque number, bank reference number, or card authorization code for payment tracking and reconciliation. Required for UPI, Bank Transfer, and Cheque payments." />
                    </Box>
                  }
                  size="small"
                  value={paymentData.transactionReference}
                  onChange={(e) =>
                    setPaymentData({
                      ...paymentData,
                      transactionReference: e.target.value,
                    })
                  }
                  placeholder={
                    paymentData.paymentMethod === "UPI" ||
                    paymentData.paymentMethod === "BANK_TRANSFER" ||
                    paymentData.paymentMethod === "CHEQUE"
                      ? "Required: Transaction ID"
                      : "Optional: Transaction ID, Cheque number, etc."
                  }
                  required={
                    paymentData.paymentMethod === "UPI" ||
                    paymentData.paymentMethod === "BANK_TRANSFER" ||
                    paymentData.paymentMethod === "CHEQUE"
                  }
                  error={submitAttempted && !!transactionRefError}
                  helperText={submitAttempted ? transactionRefError : ""}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ lg: 12, xs: 12, sm: 12, md: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  security="optional"
                  multiline
                  rows={3}
                  value={paymentData.notes}
                  onChange={(e) =>
                    setPaymentData({ ...paymentData, notes: e.target.value })
                  }
                  placeholder="Optional notes about the payment"
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          onClick={onClose}
          sx={{ fontSize: { xs: "0.7rem", sm: "1rem" } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{ fontSize: { xs: "0.7rem", sm: "1rem" } }}
        >
          Record Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selectedAssignment: PropTypes.object,
  paymentData: PropTypes.shape({
    amountPaid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentMethod: PropTypes.string,
    bankId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentDate: PropTypes.string,
    transactionReference: PropTypes.string,
    notes: PropTypes.string,
  }).isRequired,
  setPaymentData: PropTypes.func.isRequired,
  banks: PropTypes.arrayOf(
    PropTypes.shape({
      bank_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      bank_name: PropTypes.string,
    }),
  ),
  academicYearStartDate: PropTypes.string,
  academicYearEndDate: PropTypes.string,
  formatCurrency: PropTypes.func.isRequired,
};

export default PaymentDialog;
